<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <div class="pagination-container">
          <el-pagination
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            background
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
        <div class="button-container">
          <el-button
            :disabled="isLoading"
            circle
            icon="el-icon-refresh"
            @click="getList"
          />
          <el-button
            class="add-button"
            type="primary"
            icon="el-icon-edit"
            @click="handleCreate"
          >{{ $t('button.create') }}</el-button>
        </div>
      </div>
    </template>

    <div class="font-size-12 mb-2 text-center">
      <span class="font-weight-bold">NOTE:</span>
      <span class="pl-2">Scheduled Notification</span>
      <span
        class="pl-2"
        style="color: #c0c0c0"
      >Sent Notification</span>
      <span
        class="pl-2"
        style="text-decoration: line-through; color: #c0c0c0"
      >Deleted Notification</span>
    </div>

    <el-table
      v-loading="isLoading"
      :data="list"
      :row-class-name="handleTableRowClass"
      border
      stripe
    >
      <el-table-column
        v-for="{ type, ...column } in columnList"
        :key="type"
        :prop="type"
        :label="column.title || $t(`table.${type}`)"
        v-bind="column.custom"
      >
        <template slot-scope="{ row }">
          <template v-if="type === 'topic'">
            <el-tag>{{ row | formatTargetGroup }}</el-tag>
          </template>
          <template v-else-if="isDatetimeType(type)">
            {{ row[type] | formatDatetime }}
          </template>
          <template v-else>
            {{ row[type] }}
          </template>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('table.action')"
        fixed="right"
        align="center"
        width="80"
      >
        <template #default="{ row }">
          <div class="button-group">
            <el-button
              :disabled="!!(!row.deletable || row.is_delete)"
              size="mini"
              type="danger"
              icon="el-icon-delete"
              circle
              @click="handleDelete(row)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :visible.sync="deleteDialogVisible"
      :before-close="handleConfirmDialogClose"
      :title="$t('dialog.confirm')"
      width="30%"
    >
      <span>{{ $t('dialog.confirmToDelete') }}</span>
      <span slot="footer">
        <el-button
          @click="handleConfirmDialogClose"
        >{{ $t('button.cancel') }}</el-button>
        <el-button
          type="danger"
          @click="handleConfirmDelete"
        >{{ $t('button.delete') }}</el-button>
      </span>
    </el-dialog>
  </sticky-header-layout>
</template>

<script>
import useTablePaging from '../../mixins/useTablePaging'
import NotificationService from '../../api/notifications'
import {
  pushNotificationTargetGroupsMap,
  pushNotificationsColumnList as columnList,
} from '../../const/notifications'
import { parseDatetime, formatDatetime } from '../../utils/datetime'

export default {
  name: 'PushNotificationList',
  filters: {
    formatTargetGroup({ topic: value }) {
      const formatted = pushNotificationTargetGroupsMap.get(value)
      return formatted || 'Unknown'
    },
    formatDatetime(value) {
      if (!value) return ''
      return formatDatetime({ date: value })
    },
  },
  mixins: [useTablePaging],
  data() {
    return {
      columnList,
      deleteDialogVisible: false,
      deleteNotificationId: null,
    }
  },
  mounted() {
    this.getList()
  },
  beforeDestroy() {
    this.clearValidateDeletableInterval()
  },
  methods: {
    isDatetimeType(type) {
      return type === 'scheduled_at' || type === 'created_at'
    },
    isRowGreyOut({ is_delete: isDeleted, scheduled_at: scheduledDatetime }) {
      return isDeleted // is removed
        || !scheduledDatetime // pushed immediately
        || parseDatetime(scheduledDatetime) < Date.now()
    },
    isRowDeleted({ is_delete: isDeleted }) {
      return isDeleted
    },
    isWithinOneMinute(datetimeString) {
      return ((parseDatetime(datetimeString) - Date.now()) / 1000 / 60) <= 1
    },
    async getList() {
      this.isLoading = true
      try {
        const response = await NotificationService.getList({
          page: this.listQuery.page,
          size: this.listQuery.size,
        })
        this.list = response.data.map(row => ({
          ...row,
          deletable: !row.is_delete && row.scheduled_at && !this.isWithinOneMinute(row.scheduled_at),
        }))
        this.setValidateDeletableInterval()
        this.listQuery.total = response.pagination.total_items
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },
    async deleteNotification(id) {
      this.isLoading = true
      try {
        await NotificationService.delete({ id })
        const index = this.list.findIndex(row => row.id === id)
        this.list[index].is_delete = 1
        this.$message({
          message: this.$t('success.successfullyDeletedPushNotification'),
          type: 'success',
        })
      } finally {
        this.isLoading = false
      }
    },
    handleCreate() {
      this.$router.push({ path: '/push-notification/create' })
    },
    // handleUpdate({ id }) {
    //   this.$router.push({ path: `/push-notification/edit/${id}` })
    // },
    handleDelete({ id }) {
      this.deleteDialogVisible = true
      this.deleteNotificationId = id
    },
    handleConfirmDelete() {
      this.handleConfirmDialogClose()
      if (this.deleteNotificationId) {
        this.deleteNotification(this.deleteNotificationId)
      }
      this.deleteNotificationId = null
    },
    handleConfirmDialogClose() {
      this.deleteDialogVisible = false
    },
    handleTableRowClass({ row }) {
      const classList = []
      if (this.isRowGreyOut(row)) classList.push('grey')
      if (this.isRowDeleted(row)) classList.push('deleted')
      return classList.join(' ')
    },
    setValidateDeletableInterval() {
      this.clearValidateDeletableInterval()
      this.listInterval = setInterval(() => {
        this.list.forEach((row, index) => {
          if (row.deletable && this.isWithinOneMinute(row.scheduled_at)) {
            this.list[index].deletable = true
          }
        })
      }, 3000)
    },
    clearValidateDeletableInterval() {
      if (this.listInterval) {
        clearInterval(this.listInterval)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

::v-deep {
  .el-table__row {
    &.grey {
      color: #c0c0c0;

      .el-tag {
        opacity: 0.5;
      }

      &:hover {
        color: #909399;

        .el-tag {
          opacity: 1;
        }
      }
    }

    &.deleted {
      text-decoration: line-through;

      .el-tag {
        text-decoration: line-through;
      }
    }
  }
}
</style>
